import { useEffect } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ICampaign } from '@/types';
import axiosClient from '@/utils/axiosClient';
import Drawer from '../Modals/Drawer';

// Updated Zod schema with conditional validation
const schema = z.object({
  name: z.string().nonempty({ message: 'Name is required' }),
  description: z.string().nonempty({ message: 'Description is required' }),
  pathway_id: z.string().nonempty({ message: 'Pathway is required' }),
});

type FormData = z.infer<typeof schema>;

function mapDefaultValues(campaign: ICampaign) {
  return {
    name: campaign.name,
    description: campaign.description,
    pathway_id: campaign.pathway_id || '',
  };
}

interface CampaignDrawerProps {
  campaign: ICampaign;
}

const CampaignDrawer = NiceModal.create(({ campaign }: CampaignDrawerProps) => {
  const modal = useModal('campaign-drawer');
  const queryClient = useQueryClient();

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      pathway_id: '',
    },
  });

  useEffect(() => {
    if (campaign) {
      const formValues = mapDefaultValues(campaign);
      form.reset(formValues as any);
    }
  }, [campaign, form]);

  const onSubmit = async (data: FormData) => {
    try {
      // Prepare payload based on provided fields
      const payload: Partial<FormData> = {
        name: data.name,
        description: data.description,
        pathway_id: data.pathway_id,
      };

      if (campaign) {
        await axiosClient.put(`v1/campaigns/${campaign.id}`, payload);
        queryClient.invalidateQueries(`v1/campaigns/${campaign.id}`);
      } else {
        await axiosClient.post('v1/campaigns', payload);
      }

      queryClient.invalidateQueries('v1/campaigns');

      modal.hide();
    } catch (error) {
      console.error('Error saving campaigns:', error);
      if (error) {
        form.setError('pathway_id', { message: 'Invalid Pathway ID it must exist' });
      }
    }
  };

  return (
    <Drawer modal={modal} title="Campaign">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col justify-center gap-4 p-4">
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="pathway_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pathway ID</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="relative inline-flex items-center justify-end w-full gap-2 mt-2">
            <Button type="submit" disabled={form.formState.isSubmitting} className="w-full sm:w-fit">
              <Save />
              {form.formState.isSubmitting ? 'Saving...' : 'Save Campaign'}
            </Button>
          </div>
        </form>
      </Form>
    </Drawer>
  );
});

export default CampaignDrawer;
